import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Shield, Award, FileCheck, Users, Lock } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const qualificationCriteria = [
  {
    title: "Established Business",
    description: "Minimum 2 years in operation with consistent revenue",
  },
  {
    title: "Service-Based Model",
    description: "Primary revenue from consulting, advisory, or knowledge services",
  },
  {
    title: "Founder Dependency",
    description: "Business currently requires founder for credibility or delivery",
  },
  {
    title: "Scale Intent",
    description: "Clear objective to delegate, license, or prepare for exit",
  },
];

const tieringLogic = [
  {
    tier: "Foundation",
    icon: Shield,
    description: "Authority Intake Engine implementation",
    requirements: ["Initial authority audit", "Intake system design", "Basic decision logic"],
  },
  {
    tier: "Operator",
    icon: Users,
    description: "Full system deployment with operator training",
    requirements: ["Complete infrastructure", "Operator certification", "Handoff protocols"],
  },
  {
    tier: "Enterprise",
    icon: Award,
    description: "Institutional-grade governance and acquisition readiness",
    requirements: ["Governance framework", "IP documentation", "Buyer-ready systems"],
  },
];

const OperatorsPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="operators-page">
      <SEOHead 
        title="For Operators"
        description="Qualification criteria and system tiers for operators seeking institutional authority infrastructure. Not clients—system partners."
        url="https://onpointauthority.com/operators"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12" data-testid="operators-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              System Partners
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              For<br />
              <span className="text-primary">Operators</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              Operators aren't clients—they're qualified system partners. This page outlines 
              qualification criteria, system tiers, and what's required to implement 
              institutional authority infrastructure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Qualification Criteria */}
      <section className="py-24 md:py-32 px-6 md:px-12 border-t border-white/5" data-testid="qualification-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Requirements
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground">
              Qualification<br />
              <span className="text-primary">Criteria</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {qualificationCriteria.map((criteria, index) => (
              <motion.div
                key={criteria.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-transparent border border-white/5 p-8 service-card"
                data-testid={`qualification-${index}`}
              >
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-serif text-xl text-foreground mb-2">
                      {criteria.title}
                    </h3>
                    <p className="text-base font-light text-muted-foreground">
                      {criteria.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 p-6 border border-primary/20 bg-primary/5"
          >
            <p className="text-sm text-foreground">
              <span className="text-primary font-medium">Note:</span> Meeting these criteria does not 
              guarantee approval. All applications undergo internal authority review. Decisions are 
              final and non-negotiable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* System Tiers */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="tiers-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Implementation Levels
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground">
              System<br />
              <span className="text-primary">Tiers</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {tieringLogic.map((tier, index) => (
              <motion.div
                key={tier.tier}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#080808] border border-white/5 p-8 md:p-10 service-card"
                data-testid={`tier-${index}`}
              >
                <tier.icon className="h-10 w-10 text-primary mb-6" strokeWidth={1.5} />
                <p className="text-xs font-mono text-muted-foreground mb-2">TIER {index + 1}</p>
                <h3 className="font-serif text-2xl text-foreground mb-4">
                  {tier.tier}
                </h3>
                <p className="text-base font-light text-muted-foreground mb-6">
                  {tier.description}
                </p>
                <div className="border-t border-white/5 pt-6">
                  <p className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-4">
                    Includes
                  </p>
                  <ul className="space-y-2">
                    {tier.requirements.map((req, reqIndex) => (
                      <li key={reqIndex} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* License Requirements */}
      <section className="py-24 md:py-32 px-6 md:px-12 border-t border-white/5" data-testid="license-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
                Governance
              </p>
              <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-6">
                License<br />
                <span className="text-primary">Requirements</span>
              </h2>
              <p className="text-lg font-light leading-relaxed text-muted-foreground mb-8">
                Operators who implement our authority systems operate under license. 
                This ensures system integrity, maintains institutional standards, 
                and protects the value of the infrastructure.
              </p>
              <Link to="/authority-review">
                <Button
                  className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-8 py-5 rounded-none"
                  data-testid="operators-cta"
                >
                  Request Authority Review
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              {[
                {
                  icon: FileCheck,
                  title: "Documentation Compliance",
                  description: "All systems must be documented according to OnPoint standards.",
                },
                {
                  icon: Lock,
                  title: "System Integrity",
                  description: "No modifications without authorization. Maintains transferability.",
                },
                {
                  icon: Users,
                  title: "Operator Certification",
                  description: "Team members must complete certification before system access.",
                },
              ].map((item, index) => (
                <div key={item.title} className="flex items-start gap-4 p-6 border border-white/5 bg-card/5">
                  <item.icon className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <h4 className="text-foreground font-medium mb-1">{item.title}</h4>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Not a Fit Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-foreground mb-6">
              This Is <span className="text-primary">Not</span> For Everyone
            </h2>
            <p className="text-lg font-light leading-relaxed text-muted-foreground mb-8">
              If you're looking for coaching, consulting, or personality-based guidance—this isn't it. 
              We build infrastructure. We certify operators. We design systems that persist without proximity.
            </p>
            <p className="text-sm text-muted-foreground">
              If that's what you need, submit an application.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OperatorsPage;
